import { useEffect, useMemo } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { cn } from '@/shared/lib/utils'
import { t } from '@/shared/lib/i18n'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { tasksDashboardPath, nonComplianceDashboardPath, approvalsPath } from '@/shared/lib/routes'
import {
  buildDashboardLocationQuery,
  extractDashboardLocationFiltersFromUrl,
  isLocationFiltersSubset,
  readLocationFilters,
} from '@/shared/lib/locationFiltersSession'
import {
  appendNcPageFiltersToQuery,
  extractNcPageFiltersFromUrl,
  isNcPageFiltersEmpty,
  readNcPageFilters,
  writeNcPageFilters,
} from '@/shared/lib/ncPageFiltersSession'
import type { SharedProps } from '@/shared/types/SharedProps'

const DashboardTabs = () => {
  const { url, props } = usePage<SharedProps>()
  const canReadCompliance = useCan(Resources.COMPLIANCE_REPORT, Actions.READ)
  // Legacy: show Signatures when the user is on ≥1 kept chain role — not matrix alone
  // (REBUILD-596). pendingSignaturesCount only drives the red dot.
  const showSignaturesTab = props.onSignoffChain === true
  const hasUnreviewedNc = (props.unreviewedNcCount ?? 0) > 0
  const hasPendingTasks = (props.pendingTasksCount ?? 0) > 0
  const hasPendingSignatures = (props.pendingSignaturesCount ?? 0) > 0
  const onNonCompliance = url.startsWith(nonComplianceDashboardPath())

  // Carry shared location filters across Tasks ↔ NC so the destination does not
  // briefly paint an unfiltered list before session hydrate runs (REBUILD-393).
  //
  // Computed inline (not memoized on url) so a store written synchronously by an
  // in-flight Tasks navigate is reflected before the URL updates. The subset rule
  // distinguishes "store is ahead" from "store is stale":
  //   - URL ⊆ store  →  store is ahead (filter just picked, navigate not landed) → use store.
  //   - URL ⊄ store  →  store stale / bookmark / different site → use URL.
  //   - URL empty    →  trivially a subset → use store (fallback to session).
  const urlLocationFilters = extractDashboardLocationFiltersFromUrl(url)
  const storeLocationFilters = readLocationFilters(
    props.currentTenantId,
    props.currentUserInfo?.id ?? null,
  )
  const carriedLocationFilters = isLocationFiltersSubset(urlLocationFilters, storeLocationFilters)
    ? storeLocationFilters
    : urlLocationFilters
  const locationQuery = buildDashboardLocationQuery(carriedLocationFilters)

  // Persist NC-only date/search while viewing NC so Tasks → NC restores them (T-P1).
  // Never put these on the Tasks href; Clear wipes the NC session bucket.
  useEffect(() => {
    if (!onNonCompliance) return

    const fromUrl = extractNcPageFiltersFromUrl(url)
    if (!isNcPageFiltersEmpty(fromUrl)) {
      writeNcPageFilters(props.currentTenantId, props.currentUserInfo?.id ?? null, fromUrl)
    }
  }, [onNonCompliance, url, props.currentTenantId, props.currentUserInfo?.id])

  const ncPageQuery = useMemo(() => {
    if (onNonCompliance) {
      const fromUrl = extractNcPageFiltersFromUrl(url)
      if (!isNcPageFiltersEmpty(fromUrl)) {
        return appendNcPageFiltersToQuery(locationQuery, fromUrl)
      }
    }

    return appendNcPageFiltersToQuery(
      locationQuery,
      readNcPageFilters(props.currentTenantId, props.currentUserInfo?.id ?? null),
    )
  }, [onNonCompliance, url, locationQuery, props.currentTenantId, props.currentUserInfo?.id])

  const tabs = [
    {
      label: t('nav.tasks'),
      href: `${tasksDashboardPath()}${locationQuery}`,
      matchPath: tasksDashboardPath(),
      showDot: hasPendingTasks,
      dotLabel: t('nav.tasksPending'),
    },
    ...(canReadCompliance
      ? [
          {
            label: t('nav.nonCompliance'),
            href: `${nonComplianceDashboardPath()}${ncPageQuery}`,
            matchPath: nonComplianceDashboardPath(),
            showDot: hasUnreviewedNc,
            dotLabel: t('nav.nonCompliancePending'),
          },
        ]
      : []),
    ...(showSignaturesTab
      ? [
          {
            label: t('nav.pendingApprovals'),
            href: approvalsPath(),
            matchPath: approvalsPath(),
            showDot: hasPendingSignatures,
            dotLabel: t('nav.pendingApprovalsPending'),
          },
        ]
      : []),
  ]

  const tabClass = (active: boolean) =>
    cn(
      'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
      active
        ? 'border-primary text-foreground'
        : 'border-transparent text-muted-foreground hover:text-foreground',
    )

  return (
    <div className="flex border-b mb-6" role="tablist" aria-label={t('nav.dashboard')}>
      {tabs.map(({ label, href, matchPath, showDot, dotLabel }) => (
        <Link
          key={matchPath}
          href={href}
          className={cn(tabClass(url.startsWith(matchPath)), 'inline-flex items-center gap-1.5')}
        >
          {label}
          {showDot && (
            <span className="h-2 w-2 rounded-full bg-destructive" aria-label={dotLabel} />
          )}
        </Link>
      ))}
    </div>
  )
}

export default DashboardTabs
